import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Spinner,
  IconButton,
  useToast,
  Tooltip,
  useColorModeValue,
  HStack,
  VStack,
  Menu,
  MenuList,
  MenuItem,
  Center,
  Icon,
  SimpleGrid,
  Card,
  CardBody,
  useDisclosure,
  MenuButton,
  Badge,
  Portal,
} from '@chakra-ui/react';
import { RepeatIcon, CloseIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { FaCalendarAlt, FaFileUpload } from 'react-icons/fa';
import { FaFilter } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import Pagination from './Pagination';
import axiosInstance from 'axiosInstance';
import baseUrl from 'Base_Url/baseUrl';
import FileDetailsModal from './FileDetailsModal';
import ExcelEditModal from './ExcelEditModal';
import { motion } from 'framer-motion';
import UploadedFilesTable from './Table';
import SchemeModal from './SchemeModal';
import { Eye } from 'lucide-react';
import UploadStatusModal from './UploadStatusModal';

const MotionBox = motion(Box);

const getCurrentFY = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const startYear = now.getMonth() >= 3 ? currentYear : currentYear - 1;
  return `${startYear}-04-01T00:00:00.000Z`;
};

const formatDateValueToFY = (dateValue) => {
  if (!dateValue) return formatDateValueToFY(getCurrentFY());
  const year = new Date(dateValue).getFullYear();
  return `${year}-${year + 1}`;
};

const getLatestStartYear = () => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};

function UploadFile() {
  const toast = useToast();
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [user, setUser] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [schemes, setSchemes] = useState([]);
  const [fileTypes, setFileTypes] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const token = localStorage.getItem('token');
  const userData = localStorage.getItem('user');
  const role = userData ? JSON.parse(userData).role : '';

  const [isFileDetailsOpen, setIsFileDetailsOpen] = useState(false);
  const [isExcelEditOpen, setIsExcelEditOpen] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue(
    'linear(135deg, gray.50, green.50, green.100)',
    'linear(135deg, gray.900, green.100)',
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const bgGradient = useColorModeValue(
    'linear(to-br, white, teal.50)',
    'linear(to-br, gray.900, teal.900)',
  );

  const scrollbarTrackBg = useColorModeValue('pink.100', 'gray.700');
  const scrollbarThumbBg = useColorModeValue('teal.500', 'teal.300');
  const scrollbarThumbHoverBg = useColorModeValue('teal.600', 'teal.400');

  // Extract search params as separate stable values for our own use
  const searchParamValue = searchParams.get('search') || '';
  const statusParamValue = searchParams.get('status') || '';
  const startDateParamValue = searchParams.get('startDate') || '';
  const endDateParamValue = searchParams.get('endDate') || '';
  const schemeParamValue = searchParams.get('scheme') || '';
  const fileTypeParamValue = searchParams.get('fileType') || '';
  const financialYearParamValue = searchParams.get('financialYear') || getCurrentFY();

  const fetchSchemes = useCallback(async () => {
    try {
      const schemeRes = await axiosInstance.get(
        `${baseUrl}/master/all-scheme`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (schemeRes.data?.data?.data) {
        setSchemes(schemeRes.data.data.data);
      }
    } catch (error) {
      toast({
        title:
          error?.response?.data?.message ||
          error?.response?.data?.data ||
          'Error fetching schemes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [token, toast]);

  const fetchFileTypes = useCallback(
    async (schemeId) => {
      if (!schemeId) {
        setFileTypes([]);
        return;
      }
      try {
        const url = `${baseUrl}/master/all-file-type?scheme=${schemeId}&fileType=${fileTypeParamValue}`;
        const fileTypeRes = await axiosInstance.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (fileTypeRes.data?.data?.data) {
          const data = fileTypeRes.data?.data?.data;
          if (!Array.isArray(data)) {
            setFileTypes([data]);
          } else {
            setFileTypes(data);
          }
        }
      } catch (error) {
        setFileTypes([]);
        toast({
          title:
            error?.response?.data?.message ||
            error?.response?.data?.data ||
            'Error fetching file types',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [token, fileTypeParamValue, toast],
  );

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) setUser(JSON.parse(userStr));

        if (!searchParams.get('financialYear')) {
          const newParams = new URLSearchParams(searchParams);
          newParams.set('financialYear', getCurrentFY());
          setSearchParams(newParams, { replace: true });
        }

        await fetchSchemes();

        const schemeId = searchParams.get('scheme');
        if (schemeId) {
          await fetchFileTypes(schemeId);
        }
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    setTimeout(initializeData, 100);
  }, []); // Empty dependency array - runs once on mount

  // Fetch file types when scheme changes
  useEffect(() => {
    if (!isLoading) {
      const schemeId = searchParams.get('scheme');
      fetchFileTypes(schemeId);
    }
  }, [schemeParamValue, isLoading, fetchFileTypes]);

  // Set default scheme
  useEffect(() => {
    if (schemes?.length > 0 && !searchParams.get('scheme')) {
      const defaultId = String(schemes[0].id || schemes[0]._id);
      const newParams = new URLSearchParams(searchParams);
      newParams.set('scheme', defaultId);
      setSearchParams(newParams, { replace: true });
    }
  }, [schemes, searchParams, setSearchParams]);

  // Memoized debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        setPage(1);
        const newParams = new URLSearchParams(searchParams);
        if (val) newParams.set('search', val);
        else newParams.delete('search');
        newParams.set('page', 1);
        setSearchParams(newParams);
      }, 500),
    [setSearchParams], // Remove searchParams from dependencies
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const schemeId = searchParams.get('scheme');
      await fetchFileTypes(schemeId);
    } finally {
      setRefreshing(false);
    }
  }, [schemeParamValue, fetchFileTypes]);

  const handleViewClick = useCallback((file) => {
    setSelectedFile(file);
    setIsFileDetailsOpen(true);
  }, []);

  const handleEditExcelClick = useCallback((file) => {
    setEditingFile(file);
    setIsExcelEditOpen(true);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', newPage);
      setSearchParams(newParams);
    }
  }, [totalPages, searchParams, setSearchParams]);

  const handleFileUpdate = useCallback(() => {
    const schemeId = searchParams.get('scheme');
    fetchFileTypes(schemeId);
  }, [schemeParamValue, fetchFileTypes]);

  const handleFileDetailsClose = useCallback(() => {
    setIsFileDetailsOpen(false);
    setSelectedFile(null);
  }, []);

  const handleExcelEditClose = useCallback(() => {
    setIsExcelEditOpen(false);
    setEditingFile(null);
  }, []);

  const handleStatusFilter = useCallback((status) => {
    const newParams = new URLSearchParams(searchParams);
    if (status) newParams.set('status', status);
    else newParams.delete('status');
    setPage(1);
    newParams.set('page', 1);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const clearFilters = useCallback(() => {
    const currentFY = searchParams.get('financialYear');
    const newParams = new URLSearchParams();
    newParams.set('financialYear', currentFY || getCurrentFY());
    if (schemes && schemes.length > 0) {
      const defaultId = String(schemes[0].id || schemes[0]._id);
      newParams.set('scheme', defaultId);
    }
    setPage(1);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams, schemes]);

  const handleDownload = useCallback(async (fileId, fileName) => {
    try {
      const token = localStorage.getItem('token');

      const response = await axiosInstance.get(
        `${baseUrl}/upload-file/download/${fileId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        },
      );

      let downloadFileName = fileName || 'file.xlsx';
      const contentDisposition = response.headers['content-disposition'];

      if (contentDisposition) {
        let matches = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (matches && matches[1]) {
          downloadFileName = decodeURIComponent(matches[1]);
        } else {
          matches = contentDisposition.match(/filename="?([^"]+)"?/i);
          if (matches && matches[1]) {
            downloadFileName = matches[1];
          }
        }
      }

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast({
        title: 'Download started',
        description: `Downloading ${downloadFileName}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (err) {
      let errorMessage = 'Failed to download file';
      if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to download this file';
      } else if (err.response?.status === 404) {
        errorMessage = 'File not found on server, may have been deleted';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      toast({
        title: 'Download failed',
        description:
          err.response?.data?.data ||
          err.response?.data?.message ||
          'Failed to download file',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }, [toast]);

  const getAppliedFilterNames = useCallback(() => {
    const appliedFilters = [];

    if (searchParamValue) {
      appliedFilters.push(`Search: "${searchParamValue}"`);
    }

    if (statusParamValue) {
      const statusMap = {
        approved: 'Status: Approved',
        submitted: 'Status: Submitted',
        pending: 'Status: Pending',
        rejected: 'Status: Rejected',
      };
      appliedFilters.push(statusMap[statusParamValue] || `Status: ${statusParamValue}`);
    }

    if (startDateParamValue && endDateParamValue) {
      appliedFilters.push(
        `Date: ${startDateParamValue} to ${endDateParamValue}`,
      );
    } else if (startDateParamValue) {
      appliedFilters.push(`From: ${startDateParamValue}`);
    } else if (endDateParamValue) {
      appliedFilters.push(`To: ${endDateParamValue}`);
    }

    if (schemeParamValue) {
      const scheme = schemes.find(
        (s) =>
          s.id === schemeParamValue ||
          s._id === schemeParamValue ||
          String(s.id) === schemeParamValue ||
          String(s._id) === schemeParamValue,
      );

      const schemeName =
        scheme?.schemeName || scheme?.name || scheme?.title || schemeParamValue;
      appliedFilters.push(`Scheme: ${schemeName}`);
    }

    if (fileTypeParamValue) {
      if (fileTypes && fileTypes.length > 0) {
        const fileType = fileTypes.find(
          (ft) =>
            ft.id === fileTypeParamValue ||
            ft._id === fileTypeParamValue ||
            String(ft.id) === fileTypeParamValue ||
            String(ft._id) === fileTypeParamValue,
        );
        const fileTypeName =
          fileType?.name || fileType?.fileType || fileType?.title || fileTypeParamValue;
        appliedFilters.push(`Type: ${fileTypeName}`);
      } else {
        appliedFilters.push(`Type: ${fileTypeParamValue}`);
      }
    }

    if (financialYearParamValue) {
      appliedFilters.push(
        `FY: ${formatDateValueToFY(financialYearParamValue)}`,
      );
    } else {
      appliedFilters.push(`FY: ${getCurrentFY()} (Default)`);
    }

    return appliedFilters;
  }, [
    searchParamValue,
    statusParamValue,
    startDateParamValue,
    endDateParamValue,
    schemeParamValue,
    fileTypeParamValue,
    financialYearParamValue,
    schemes,
    fileTypes
  ]);

  const loaderBg = useColorModeValue('gray.50', 'gray.900');
  const loaderPattern = useColorModeValue(
    'radial-gradient(circle at 25px 25px, rgba(120, 119, 198, 0.3) 2%, transparent 0%), radial-gradient(circle at 75px 75px, rgba(120, 119, 198, 0.3) 2%, transparent 0%)',
    'radial-gradient(circle at 25px 25px, rgba(66, 153, 225, 0.15) 2%, transparent 0%), radial-gradient(circle at 75px 75px, rgba(66, 153, 225, 0.15) 2%, transparent 0%)',
  );

  const loaderGradient = useColorModeValue(
    'linear(to-r, teal.400, cyan.500)',
    'linear(to-r, teal.500, cyan.600)',
  );

  // Memoize the UploadedFilesTable to prevent unnecessary re-renders
  const uploadedFilesTable = useMemo(() => (
    <UploadedFilesTable
      SchemeModalOpen={onOpen}
      SchemeModalClose={onClose}
      isOpenDetails={isFileDetailsOpen}
      onCloseDetails={handleFileDetailsClose}
      files={fileTypes}
      currentPage={page}
      itemsPerPage={10}
      onViewClick={handleViewClick}
      onEditClick={handleEditExcelClick}
      onDownloadClick={handleDownload}
      userRole={role}
      searchParams={searchParams} // Pass the actual searchParams
      onClearFilters={clearFilters}
      isLoading={loading}
    />
  ), [
    onOpen,
    onClose,
    isFileDetailsOpen,
    handleFileDetailsClose,
    fileTypes,
    page,
    handleViewClick,
    handleEditExcelClick,
    handleDownload,
    role,
    searchParams.toString(), // Use stringified version for comparison
    clearFilters,
    loading
  ]);

  const appliedFilters = useMemo(() => getAppliedFilterNames(), [getAppliedFilterNames]);

  if (isLoading) {
    return (
      <Flex
        minH="100vh"
        align="center"
        justify="center"
        bg={loaderBg}
        position="relative"
        overflow="hidden"
        borderRadius="20px"
      >
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          backgroundImage={loaderPattern}
          backgroundSize="100px 100px"
        />

        <Flex direction="column" align="center" gap={6}>
          <Box
            width="100px"
            height="100px"
            borderRadius="full"
            bgGradient={loaderGradient}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FaFileUpload} boxSize={12} color="white" />
          </Box>
        </Flex>
      </Flex>
    );
  }

  return (
    <Box
      bgGradient={bgColor}
      minH="calc(100vh - 64px)"
      p={4}
      // pt={'90px'}
      borderRadius="2xl"
    >
      <MotionBox
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        mb={2}
      >
        <Flex
          direction="column"
          p={4}
          borderRadius="2xl"
          bgGradient={bgGradient}
          boxShadow="0 10px 40px rgba(0, 0, 0, 0.05)"
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="-50px"
            right="-50px"
            w="200px"
            h="200px"
            borderRadius="full"
            bgGradient="linear(to-br, teal.200, cyan.200)"
            opacity={0.1}
          />
          <Box
            position="absolute"
            bottom="-30px"
            left="-30px"
            w="150px"
            h="150px"
            borderRadius="full"
            bgGradient="linear(to-br, blue.200, purple.200)"
            opacity={0.1}
          />

          <Flex
            justify="space-between"
            align="center"
            // mb={4}
            position="relative"
            zIndex={1}
          >
            <Flex align="center">
              <Flex
                align="center"
                justify="center"
                w="40px"
                h="40px"
                borderRadius="xl"
                bgGradient="linear(to-br, teal.500, cyan.600)"
                mr={4}
              >
                <Icon as={FaFileUpload} boxSize={4} color="white" />
              </Flex>
              <VStack align="start" spacing={1}>
                <Text fontSize="xl" fontWeight="bold" color={textColor}>
                  Uploaded Data
                </Text>
                <Text color={subtitleColor} fontSize="sm">
                  Manage and track uploaded Data
                </Text>
              </VStack>
            </Flex>

            <HStack spacing={2} pt="60px">
              <Tooltip label="Refresh" hasArrow>
                <IconButton
                  aria-label="Refresh"
                  icon={
                    refreshing ? (
                      <Spinner
                        size="xs"
                        color="white"
                        thickness="2px"
                        speed="0.65s"
                      />
                    ) : (
                      <RepeatIcon />
                    )
                  }
                  onClick={handleRefresh}
                  isDisabled={refreshing}
                  colorScheme="teal"
                  variant="solid"
                  size="md"
                  borderRadius="3xl"
                  className={refreshing ? 'rotate-animation' : ''}
                  boxShadow="0 4px 15px rgba(0, 150, 136, 0.2)"
                  _hover={{
                    transform: 'translateY(-2px)',
                    
                    boxShadow: '0 10px 25px rgba(0, 150, 136, 0.3)',
                  }}
                  _disabled={{
                    opacity: 1,
                    cursor: 'not-allowed',
                  }}
                  transition="all 0.2s"
                />
              </Tooltip>
            </HStack>
          </Flex>

          <SimpleGrid
            columns={{ base: 2, md: 4 }}
            spacing={4}
            mt={4}
            position="relative"
            zIndex={1}
          >
          </SimpleGrid>
        </Flex>
      </MotionBox>

      <Card
        mb={2}
        boxShadow="md"
        borderRadius="xl"
        bg={cardBg}
        position="relative"
        overflow="hidden"
      >
        <CardBody position="relative" pb={0}>
          <Box
            position="absolute"
            top="-50px"
            right="-50px"
            w="200px"
            h="200px"
            borderRadius="full"
            bgGradient="linear(to-br, teal.200, cyan.200)"
            opacity={0.1}
            zIndex={0}
          />
          <Box
            position="absolute"
            bottom="-30px"
            left="-30px"
            w="150px"
            h="150px"
            borderRadius="full"
            bgGradient="linear(to-br, blue.200, purple.200)"
            opacity={0.1}
            zIndex={0}
          />

          <Flex
            direction={{ base: 'column', md: 'row' }}
            // gap={4}
            align={{ md: 'center' }}
            position="relative"
            zIndex={1}
          // mb={4}
          >
            <Text
              fontSize="md"
              fontWeight="bold"
              color={textColor}
              whiteSpace="nowrap"
            >
              Scheme :
            </Text>
            <Box
              width="100%"
              overflowX="auto"
              whiteSpace="nowrap"
              pb={2}
              position="relative"
              cursor="grab"
              _active={{ cursor: 'grabbing' }}
              onMouseDown={(e) => {
                const container = e.currentTarget;
                let isDown = false;
                let startX;
                let scrollLeft;

                const mouseDownHandler = (e) => {
                  isDown = true;
                  container.style.cursor = 'grabbing';
                  startX = e.pageX - container.offsetLeft;
                  scrollLeft = container.scrollLeft;
                };

                const mouseLeaveHandler = () => {
                  isDown = false;
                  container.style.cursor = 'grab';
                };

                const mouseUpHandler = () => {
                  isDown = false;
                  container.style.cursor = 'grab';
                };

                const mouseMoveHandler = (e) => {
                  if (!isDown) return;
                  e.preventDefault();
                  const x = e.pageX - container.offsetLeft;
                  const walk = (x - startX) * 2;
                  container.scrollLeft = scrollLeft - walk;
                };

                container.addEventListener('mousedown', mouseDownHandler);
                container.addEventListener('mouseleave', mouseLeaveHandler);
                container.addEventListener('mouseup', mouseUpHandler);
                container.addEventListener('mousemove', mouseMoveHandler);

                e.currentTarget._dragHandlers = {
                  mouseDown: mouseDownHandler,
                  mouseLeave: mouseLeaveHandler,
                  mouseUp: mouseUpHandler,
                  mouseMove: mouseMoveHandler,
                };
              }}
              onMouseLeave={(e) => {
                if (e.currentTarget._dragHandlers) {
                  const container = e.currentTarget;
                  container.style.cursor = 'grab';

                  container.removeEventListener(
                    'mousedown',
                    e.currentTarget._dragHandlers.mouseDown,
                  );
                  container.removeEventListener(
                    'mouseleave',
                    e.currentTarget._dragHandlers.mouseLeave,
                  );
                  container.removeEventListener(
                    'mouseup',
                    e.currentTarget._dragHandlers.mouseUp,
                  );
                  container.removeEventListener(
                    'mousemove',
                    e.currentTarget._dragHandlers.mouseMove,
                  );
                  delete e.currentTarget._dragHandlers;
                }
              }}
              sx={{
                '&::-webkit-scrollbar': {
                  height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: scrollbarTrackBg,
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: scrollbarThumbBg,
                  borderRadius: '10px',
                  '&:hover': {
                    background: scrollbarThumbHoverBg,
                  },
                },
                '&::-webkit-scrollbar': {
                  height: '6px',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                },
                '&:hover::-webkit-scrollbar': {
                  opacity: 1,
                },
                scrollbarWidth: 'thin',
                scrollbarColor: `${scrollbarThumbBg} ${scrollbarTrackBg}`,
              }}
            >
              <HStack
                spacing={1}
                display="inline-flex"
                minWidth="min-content"
                pr={2}
              >
                {schemes?.map((scheme, index) => {
                  const schemeId = String(scheme.id || scheme._id);
                  const isActive =
                    searchParams.get('scheme') === schemeId ||
                    (!searchParams.get('scheme') && index === 0);

                  return (
                    <Button
                      key={schemeId}
                      colorScheme="teal"
                      variant={isActive ? 'solid' : 'outline'}
                      borderRadius="2xl"
                      size="sm"
                      flexShrink={0}
                      onClick={(e) => {
                        if (
                          e.currentTarget
                            .closest('.scheme-scroll-container')
                            ?.getAttribute('data-dragging') === 'true'
                        ) {
                          return;
                        }
                        const newParams = new URLSearchParams();
                        newParams.set('scheme', schemeId);
                        newParams.set('page', 1);
                        newParams.set('financialYear', searchParams.get('financialYear') || getCurrentFY());
                        setSearchParams(newParams);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      position="relative"
                      _before={
                        isActive
                          ? {
                            content: '""',
                            position: 'absolute',
                            bottom: '-6px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '20px',
                            height: '3px',
                            bg: 'teal.500',
                            borderRadius: 'full',
                          }
                          : {}
                      }
                    >
                      {scheme.schemeName || scheme.name || scheme.title}
                    </Button>
                  );
                })}
              </HStack>
            </Box>
          </Flex>
          <SimpleGrid
            columns={{ base: 1, md: 3, lg: 5 }}
            spacing={3}
            mb={4}
            position="relative"
            zIndex={1}
          >
            <Menu isLazy>
              <MenuButton
                as={Button}
                size="sm"
                variant="outline"
                leftIcon={<Icon as={FaCalendarAlt} color="orange.400" />}
                rightIcon={<ChevronDownIcon />}
                borderColor="orange.200"
                zIndex={1}
                _hover={{ borderColor: 'orange.400' }}
              >
                FY:{' '}
                {formatDateValueToFY(financialYearParamValue)}
              </MenuButton>

              <Portal>
                <MenuList
                  maxH="200px"
                  overflowY="auto"
                  zIndex={1500}
                  boxShadow="lg"
                  bg={cardBg}
                  borderColor={borderColor}
                >
                  {(() => {
                    const latestStart = getLatestStartYear();
                    const startFrom = 2023;
                    const totalYears = latestStart - startFrom + 1;

                    return [...Array(totalYears)].map((_, i) => {
                      const year = latestStart - i;
                      const fyLabel = `${year}-${year + 1}`;
                      const fyISOValue = `${year}-04-01T00:00:00.000Z`;

                      return (
                        <MenuItem
                          key={fyISOValue}
                          onClick={() => {
                            const newParams = new URLSearchParams(searchParams);
                            newParams.set('financialYear', fyISOValue);
                            newParams.set('page', 1);
                            setSearchParams(newParams);
                          }}
                        >
                          <HStack justify="space-between" w="100%">
                            <Text fontSize="sm">{fyLabel}</Text>
                            {fyISOValue === getCurrentFY() && (
                              <Badge
                                colorScheme="orange"
                                variant="subtle"
                                fontSize="xs"
                              >
                                Current
                              </Badge>
                            )}
                          </HStack>
                        </MenuItem>
                      );
                    });
                  })()}
                </MenuList>
              </Portal>
            </Menu>
            <Button
              leftIcon={<FaFilter />}
              size="sm"
              colorScheme="teal"
              variant="solid"
              onClick={onOpen}
            >
              Select File Type
            </Button>
            {role === "schemeAdmin" ? (
              <div style={{ display: getAppliedFilterNames()?.[0] === 'Scheme: Land Acquisition & Big Projects' ? 'none' : 'block' }}>
                <Button
                  leftIcon={<Eye />}
                  size="sm"
                  colorScheme="teal"
                  variant="solid"
                  onClick={() => setIsStatusModalOpen(true)}
                >
                  MIS Report
                </Button>
              </div>
            ) : ''}



          </SimpleGrid>

          {(searchParamValue ||
            startDateParamValue ||
            endDateParamValue ||
            statusParamValue ||
            schemeParamValue ||
            fileTypeParamValue) && (
              <Flex
                align="center"
                justify="space-between"
                mt={2}
                py={3}
                borderTop="1px solid"
                borderColor={borderColor}
              >
                <HStack spacing={2} flexWrap="wrap">
                  <Text fontSize="sm" fontWeight="bold" color={subtitleColor}>
                    Filters:
                  </Text>
                  {appliedFilters.map((filter, index) => (
                    <Badge
                      key={index}
                      colorScheme="pink"
                      variant="subtle"
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      {filter}
                    </Badge>
                  ))}
                </HStack>
                <Button
                  size="xs"
                  onClick={clearFilters}
                  colorScheme="red"
                  variant="ghost"
                  leftIcon={<CloseIcon />}
                >
                  Clear
                </Button>
              </Flex>
            )}
        </CardBody>
      </Card>

      <Box
        bg={cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
        boxShadow="sm"
      >
        {loading ? (
          <Center py={8}>
            <VStack spacing={3}>
              <Spinner size="md" color="blue.500" />
              <Text color={subtitleColor}>Loading files...</Text>
            </VStack>
          </Center>
        ) : (
          <>
            <Box overflowX="auto">
              {uploadedFilesTable}
            </Box>

            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              colorScheme="blue"
              size="sm"
              showInfo={true}
              mt={4}
              mb={2}
            />
          </>
        )}
      </Box>

      <FileDetailsModal
        isOpen={isFileDetailsOpen}
        onClose={handleFileDetailsClose}
        selectedFile={selectedFile}
        onFileUpdate={handleFileUpdate}
        user={user}
      />
      <UploadStatusModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        schemeId={schemeParamValue}
        financialYear={financialYearParamValue}
      />
      <ExcelEditModal
        currentFY={financialYearParamValue}
        isOpen={isExcelEditOpen}
        onClose={handleExcelEditClose}
        editingFile={editingFile}
        onFileUpdate={handleFileUpdate}
        user={user}
      />

      <SchemeModal
        isOpen={isOpen}
        onClose={onClose}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        setPage={setPage}
        schemes={schemes}
        fileTypes={fileTypes}
        onFileTypesFetched={(fetchedFileTypes) => {
          setFileTypes(fetchedFileTypes);
        }}
      />

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .rotate-animation {
            animation: spin 0.6s ease-in-out;
          }
        `}
      </style>
    </Box>
  );
}

export default React.memo(UploadFile);